import React, { useState, useEffect } from 'react';
import { getEntityDetails, getIncidentsByEntity, updateIncident } from '../services/apiService';
import { Entity, Incident, User, EntityType, Driver, Operator, Taxi, IncidentStatus, Severity } from '../types';
import { LoadingSpinner, AlertTriangleIcon } from './icons';
import IncidentDetailModal from './IncidentDetailModal';

interface EntityProfilePageProps {
  user: User;
  entityInfo: { id: string; type: EntityType };
  onBack: () => void;
}

const DetailItem: React.FC<{ label: string; value?: string | number }> = ({ label, value }) => (
    <div className="flex flex-col sm:flex-row sm:py-2">
        <dt className="text-sm font-medium text-gray-500 w-full sm:w-1/3 flex-shrink-0">{label}</dt>
        <dd className="text-sm text-gray-900 w-full sm:w-2/3 break-words">{value || 'N/A'}</dd>
    </div>
);

const statusColors: Record<IncidentStatus, string> = {
  [IncidentStatus.OPEN]: 'bg-blue-100 text-blue-800',
  [IncidentStatus.UNDER_REVIEW]: 'bg-yellow-100 text-yellow-800',
  [IncidentStatus.DISPUTED]: 'bg-orange-100 text-orange-800',
  [IncidentStatus.RESOLVED]: 'bg-green-100 text-green-800',
};

const severityColors: Record<Severity, string> = {
    [Severity.MINOR]: 'bg-gray-200 text-gray-800',
    [Severity.MAJOR]: 'bg-yellow-400 text-yellow-900',
    [Severity.CRITICAL]: 'bg-red-500 text-white',
};

const IncidentHistoryCard: React.FC<{ incident: Incident; onClick: () => void }> = ({ incident, onClick }) => (
    <div onClick={onClick} className="bg-white p-4 rounded-lg shadow border border-gray-200 cursor-pointer hover:shadow-md transition-shadow">
        <div className="flex justify-between items-start mb-2">
            <p className="font-bold text-gray-800">{incident.incidentType}</p>
            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColors[incident.status]}`}>
                {incident.status}
            </span>
        </div>
        <div className="text-sm text-gray-600 mb-2">
            <p><span className="font-semibold">Date:</span> {new Date(incident.incidentDate).toLocaleDateString()}</p>
            <p><span className="font-semibold">Reported by:</span> {incident.reporterOrgName}</p>
        </div>
        <div className="flex justify-end items-center text-xs text-gray-500">
            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${severityColors[incident.severity]}`}>
                {incident.severity}
            </span>
        </div>
    </div>
);


const EntityProfilePage: React.FC<EntityProfilePageProps> = ({ user, entityInfo, onBack }) => {
  const [entity, setEntity] = useState<Entity | null>(null);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [entityData, incidentsData] = await Promise.all([
          getEntityDetails(entityInfo.id),
          getIncidentsByEntity(entityInfo.id),
        ]);
        setEntity(entityData);
        setIncidents(incidentsData);
      } catch (err) {
        setError('Failed to fetch entity details.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [entityInfo.id]);

  const handleIncidentUpdate = (updatedIncident: Incident) => {
    setIncidents(prev => prev.map(inc => inc.id === updatedIncident.id ? updatedIncident : inc));
    setSelectedIncident(updatedIncident);
  };

  const handleIncidentDelete = (incidentId: string) => {
    setIncidents(prev => prev.filter(inc => inc.id !== incidentId));
  };

  const renderEntityDetails = () => {
    if (!entity) return null;
    return (
        <dl>
            <DetailItem label="Name" value={entity.name} />
            {'licenceNumber' in entity && <DetailItem label="Licence #" value={(entity as Driver).licenceNumber} />}
            {'dateOfBirth' in entity && <DetailItem label="Date of Birth" value={(entity as Driver).dateOfBirth} />}
            {'abn' in entity && <DetailItem label="ABN" value={(entity as Operator).abn} />}
            {'plateNumber' in entity && <DetailItem label="Plate #" value={(entity as Taxi).plateNumber} />}
            {'vin' in entity && <DetailItem label="VIN" value={(entity as Taxi).vin} />}
            {'make' in entity && <DetailItem label="Make/Model" value={`${(entity as Taxi).make} ${(entity as Taxi).model} (${(entity as Taxi).year})`} />}
            {'contact' in entity && <DetailItem label="Contact" value={entity.contact} />}
        </dl>
    );
  };

  if (loading) {
    return <div className="flex justify-center items-center h-full"><LoadingSpinner className="w-10 h-10 text-primary" /></div>;
  }
  if (error) {
    return <div className="text-center text-red-500">{error}</div>;
  }
  if (!entity) {
    return <div className="text-center text-gray-500">Entity not found.</div>;
  }

  return (
    <div>
        <button onClick={onBack} className="mb-6 text-sm font-medium text-primary hover:text-primary-dark flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Back to List
        </button>

        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 break-words">{entity.name}</h1>
            <p className="text-md text-gray-500 mb-4">{entityInfo.type} Profile</p>
            <div className="border-t border-gray-200 pt-4">
                {renderEntityDetails()}
            </div>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Incident History</h2>
             {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
                 <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                    <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Incident ID</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Severity</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reported By</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                    {incidents.length > 0 ? incidents.map(incident => (
                        <tr key={incident.id} onClick={() => setSelectedIncident(incident)} className="hover:bg-gray-50 cursor-pointer">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-700">{incident.id}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{incident.incidentType}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${severityColors[incident.severity]}`}>
                            {incident.severity}
                            {incident.severity === Severity.CRITICAL && <AlertTriangleIcon className="w-4 h-4 ml-1 inline-block" />}
                            </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColors[incident.status]}`}>
                            {incident.status}
                            </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{incident.reporterOrgName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(incident.incidentDate).toLocaleDateString()}</td>
                        </tr>
                    )) : (
                        <tr>
                            <td colSpan={6} className="text-center text-gray-500 py-8">No incidents found for this entity.</td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>
             {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
                {incidents.length > 0 ? incidents.map(incident => (
                    <IncidentHistoryCard 
                        key={incident.id} 
                        incident={incident} 
                        onClick={() => setSelectedIncident(incident)}
                    />
                )) : (
                    <p className="text-center text-gray-500 py-8">No incidents found for this entity.</p>
                )}
            </div>
        </div>
        {selectedIncident && (
            <IncidentDetailModal 
            incident={selectedIncident} 
            user={user} 
            onClose={() => setSelectedIncident(null)}
            onUpdate={handleIncidentUpdate}
            onDelete={handleIncidentDelete}
            />
        )}
    </div>
  );
};

export default EntityProfilePage;
